import { Shield, User, Wrench } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import { ActionConfirmation, PendingAction } from './ActionConfirmation';
import { SafeImageFallback } from '@/components/ui/SafeImageFallback';
import type { ContextQuestion } from '@/lib/contextQuestion';
import { parseContextQuestion } from '@/lib/contextQuestion';
import { ContextQuestionForm } from './ContextQuestionForm';
import { RetryConfirmation, RetryAction } from './RetryConfirmation';
import { detectMissingForm } from '@/lib/agentFormInjector';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  toolCalls?: string[];
  isStreaming?: boolean;
  pendingAction?: PendingAction;
  contextQuestion?: ContextQuestion;
  contextQuestionAnswered?: boolean;
  retryAction?: RetryAction;
  retryActioned?: boolean;
}

interface AgentMessageProps {
  message: Message;
  /** Previous messages for context extraction (used for form injection fallback) */
  messageHistory?: Message[];
  onConfirmAction?: (actionId: string) => void;
  onCancelAction?: (actionId: string) => void;
  onContextQuestionSubmit?: (questionId: string, answers: Record<string, string>) => void;
  onRetryAction?: (action: RetryAction) => void;
  onDismissRetry?: (actionId: string) => void;
  isLoading?: boolean;
}

export function AgentMessage({ message, messageHistory, onConfirmAction, onCancelAction, onContextQuestionSubmit, onRetryAction, onDismissRetry, isLoading }: AgentMessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  // Use pre-parsed context question from API if available, otherwise try parsing content
  // API-provided contextQuestion is cleaner and more reliable
  // Also apply form injection fallback if AI forgot to include the form
  const { cleanContent, question } = (() => {
    if (!isAssistant || message.contextQuestionAnswered) {
      return { cleanContent: message.content, question: null };
    }
    
    // Prefer structured data from API response
    if (message.contextQuestion) {
      // Still need to strip markers from content in case any remain
      const { cleanContent } = parseContextQuestion(message.content);
      return { cleanContent, question: message.contextQuestion };
    }
    
    // Try parsing from content
    const parsed = parseContextQuestion(message.content);
    
    // If no question found but AI seems to be offering an action, try injection
    if (!parsed.question && !message.isStreaming) {
      // Convert message history to the format expected by detectMissingForm
      const historyContext = messageHistory?.map(m => ({
        role: m.role,
        content: m.content,
      }));
      
      const injectionResult = detectMissingForm(parsed.cleanContent, false, historyContext);
      if (injectionResult.wasInjected && injectionResult.injectedQuestion) {
        return { 
          cleanContent: injectionResult.content, 
          question: injectionResult.injectedQuestion 
        };
      }
      return { cleanContent: injectionResult.content, question: null };
    }
    
    return parsed;
  })();

  const handleQuestionSubmit = (answers: Record<string, string>) => {
    if (question && onContextQuestionSubmit) {
      onContextQuestionSubmit(question.id, answers);
    }
  };

  return (
    <div
      className={cn(
        "flex gap-3",
        isUser && "flex-row-reverse"
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          "shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
          isUser ? "agent-avatar-user" : "agent-avatar-assistant"
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-white" />
        ) : (
          <Shield className="h-4 w-4 text-primary" />
        )}
      </div>

      {/* Message content */}
      <div
        className={cn(
          "flex-1 space-y-1.5 max-w-[85%]",
          isUser && "flex flex-col items-end"
        )}
      >
        {/* Tool calls indicator */}
        {isAssistant && message.toolCalls && message.toolCalls.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mb-1">
            {summariseToolCalls(message.toolCalls).map(({ name, count }) => (
              <Badge key={name} variant="outline" className="text-xs gap-1.5 agent-tool-badge py-1">
                <Wrench className="h-3 w-3" />
                {formatToolName(name)}{count > 1 ? ` ×${count}` : ''}
              </Badge>
            ))}
          </div>
        )}

        {/* Message bubble */}
        <div
          className={cn(
            "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
            isUser 
              ? "agent-message-user text-white rounded-tr-md" 
              : "agent-message-assistant rounded-tl-md",
            message.isStreaming && "animate-pulse"
          )}
        >
          {isAssistant ? (
            <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2">
              <ReactMarkdown
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  code: ({ children }) => (
                    <code className="bg-primary/10 text-primary px-1.5 py-0.5 rounded-md text-xs font-mono">{children}</code>
                  ),
                  pre: ({ children }) => (
                    <pre className="bg-muted/80 p-3 rounded-lg overflow-x-auto text-xs my-2">{children}</pre>
                  ),
                  a: ({ href, children }) => (
                    <a 
                      href={href} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
                    >
                      {children}
                    </a>
                  ),
                  img: ({ src, alt }) => {
                    // Skip data URIs entirely - they shouldn't be in chat
                    const srcStr = typeof src === 'string' ? src : '';
                    if (!srcStr || srcStr.startsWith('data:')) {
                      return null;
                    }
                    return (
                      <div className="my-2 flex items-center gap-3">
                        <SafeImageFallback
                          src={srcStr}
                          alt={typeof alt === 'string' ? alt : 'Supplier logo'}
                          className="h-10 w-10 rounded-md object-contain bg-muted/40"
                          containerClassName="h-10 w-10 rounded-md bg-muted/40"
                          fallbackSize="md"
                        />
                        {alt ? (
                          <span className="text-xs text-muted-foreground">{alt}</span>
                        ) : null}
                      </div>
                    );
                  },
                  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                  h1: ({ children }) => <h4 className="font-semibold text-base mt-3 mb-2">{children}</h4>,
                  h2: ({ children }) => <h5 className="font-semibold text-sm mt-3 mb-1.5">{children}</h5>,
                  h3: ({ children }) => <h6 className="font-medium text-sm mt-2 mb-1">{children}</h6>,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-primary/30 pl-3 italic text-muted-foreground my-2">
                      {children}
                    </blockquote>
                  ),
                }}
              >
                {cleanContent || '...'}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="whitespace-pre-wrap">{message.content}</p>
          )}
        </div>

        {/* Context question form - show interactive inputs */}
        {isAssistant && question && !message.contextQuestionAnswered && (
          <ContextQuestionForm
            question={question}
            onSubmit={handleQuestionSubmit}
            isDisabled={message.isStreaming}
          />
        )}

        {/* Action confirmation buttons */}
        {isAssistant && message.pendingAction && onConfirmAction && onCancelAction && (
          <ActionConfirmation
            action={message.pendingAction}
            onConfirm={onConfirmAction}
            onCancel={onCancelAction}
          />
        )}

        {/* Retry confirmation for failed actions */}
        {isAssistant && message.retryAction && !message.retryActioned && onRetryAction && onDismissRetry && (
          <RetryConfirmation
            retryAction={message.retryAction}
            onRetry={onRetryAction}
            onDismiss={onDismissRetry}
            isLoading={isLoading}
          />
        )}

        {/* Timestamp */}
        <p className={cn(
          "text-[11px] text-muted-foreground/70 px-1",
          isUser && "text-right"
        )}>
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
}

function summariseToolCalls(tools: string[]): { name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const t of tools) {
    counts.set(t, (counts.get(t) || 0) + 1);
  }
  return Array.from(counts, ([name, count]) => ({ name, count }));
}

function formatToolName(tool: string): string {
  return tool
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
